import React, { useState, useRef, useEffect } from 'react';

// ─── Helpers ───────────────────────────────────────────────────────────────────

function parseRecommendation(text) {
  const match = text.match(/<recommendation>([\s\S]*?)<\/recommendation>/);
  if (!match) return null;
  try { return JSON.parse(match[1]); } catch { return null; }
}

function stripRecommendation(text) {
  return text.replace(/<recommendation>[\s\S]*?<\/recommendation>/g, '').trim();
}

// Render text with **bold** markdown support
function renderMarkdown(text) {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

// ─── Main screen ───────────────────────────────────────────────────────────────

export default function SymptomScreen({ navigate, goBack, saveSymptomSession, user, onAuthPrompt }) {
  const [messages,      setMessages]      = useState([]);  // [{role, content}]
  const [input,         setInput]         = useState('');
  const [phase,         setPhase]         = useState('input');    // input | chatting | result
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming,   setIsStreaming]   = useState(false);
  const [result,        setResult]        = useState(null);
  const [error,         setError]         = useState(null);

  const bottomRef = useRef(null);
  const resultRef = useRef(null);
  const inputRef  = useRef(null);
  const replyRef  = useRef(null);

  // Re-focus reply input after each send (streaming complete or after phase change)
  useEffect(() => {
    if (phase === 'chatting' && !isStreaming) {
      replyRef.current?.focus();
    }
  }, [phase, isStreaming]);

  // Auto-scroll as new content arrives
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  async function sendMessage(text) {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;

    const newMessages = [...messages, { role: 'user', content: trimmed }];
    setMessages(newMessages);
    setInput('');
    setPhase('chatting');
    setIsStreaming(true);
    setStreamingText('');
    setError(null);

    try {
      const response = await fetch('/api/triage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: newMessages }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(`${response.status}${errText ? ': ' + errText : ''}`);
      }

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText  = '';
      let buffer    = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete last line

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;
          try {
            const event = JSON.parse(data);
            if (event.type === 'content_block_delta' && event.delta?.type === 'text_delta') {
              fullText += event.delta.text;
              setStreamingText(fullText);
            }
          } catch { /* ignore malformed SSE lines */ }
        }
      }

      // Stream complete — commit assistant message
      const rec         = parseRecommendation(fullText);
      const finalText   = fullText;
      setMessages(prev => [...prev, { role: 'assistant', content: finalText }]);
      setStreamingText('');
      setIsStreaming(false);

      if (rec) {
        setResult(rec);
        setPhase('result');
        if (saveSymptomSession) {
          const firstUserMsg = newMessages[0]?.content || '';
          saveSymptomSession([], firstUserMsg, {}, rec.level, rec.icd10 || null, rec.condition || null);
        }
        setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 150);
      }
    } catch (err) {
      setIsStreaming(false);
      setStreamingText('');
      setError(`Error: ${err.message}`);
      console.error('[triage]', err);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }

  // Strip the raw <recommendation> block from display text
  const displayMessages = messages
    .map(m => ({
      ...m,
      display: m.role === 'assistant' ? stripRecommendation(m.content) : m.content,
    }))
    .filter(m => m.display.trim());

  const streamDisplay = stripRecommendation(streamingText);

  return (
    <div style={s.screen}>

      {/* Header */}
      <div style={s.header}>
        <div style={s.accentBar} />
        <button style={s.back} onClick={goBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <div style={s.title}>What's <em style={{ fontStyle: 'italic', color: 'var(--teal)' }}>going on?</em></div>
        <div style={s.subtitle}>Describe your symptoms and get guidance on what to do.</div>
      </div>

      {/* Disclaimer — always visible */}
      <div style={s.disclaimer}>
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.1"/>
          <path d="M6.5 4v3.5M6.5 9v.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
        </svg>
        For informational guidance only — not a diagnosis. Always see a real provider.
      </div>

      {/* Scrollable body */}
      <div style={s.body}>
        <div style={s.bodyInner}>

          {/* ── Input phase ── */}
          {phase === 'input' && (
            <>
              <div style={s.section}>
                <div style={s.sectionLabel}>Describe your symptoms</div>
                <textarea
                  ref={inputRef}
                  style={s.textarea}
                  rows={4}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="e.g. My daughter has had a fever of 102° since yesterday evening and is complaining of a sore throat…"
                  autoFocus
                />
              </div>
              <button
                style={{ ...s.primaryBtn, opacity: input.trim() ? 1 : 0.35 }}
                onClick={() => sendMessage(input)}
                disabled={!input.trim()}
              >
                Get guidance →
              </button>
            </>
          )}

          {/* ── Chat phase ── */}
          {phase !== 'input' && (
            <div style={s.chatCard}>
              <div style={s.chat}>
                {displayMessages.map((msg, i) => (
                  <div key={i} style={{ ...s.bubbleWrap, ...(msg.role === 'user' ? s.bubbleWrapUser : {}) }}>
                    {msg.role === 'assistant' && (
                      <div style={s.bubbleFrom}>ClearPath</div>
                    )}
                    <div style={{ ...s.bubble, ...(msg.role === 'user' ? s.bubbleUser : s.bubbleAssistant) }}>
                      {msg.role === 'assistant' ? renderMarkdown(msg.display) : msg.display}
                    </div>
                  </div>
                ))}

                {/* Streaming assistant message */}
                {isStreaming && (
                  <div style={s.bubbleWrap}>
                    <div style={s.bubbleFrom}>ClearPath</div>
                    {streamDisplay ? (
                      <div style={s.bubbleAssistant}>
                        {renderMarkdown(streamDisplay)}<span style={s.cursor} />
                      </div>
                    ) : (
                      <div style={{ ...s.bubbleAssistant, ...s.typingBubble }}>
                        <span style={{ ...s.dot, animationDelay: '0s' }} />
                        <span style={{ ...s.dot, animationDelay: '0.2s' }} />
                        <span style={{ ...s.dot, animationDelay: '0.4s' }} />
                      </div>
                    )}
                  </div>
                )}

                {error && <div style={s.errorMsg}>{error}</div>}

                <div ref={bottomRef} />
              </div>
            </div>
          )}

          {/* ── Result card ── */}
          {phase === 'result' && result && (
            <div ref={resultRef}>
              <ResultCard result={result} navigate={navigate} user={user} onAuthPrompt={onAuthPrompt} />
            </div>
          )}

        </div>
      </div>

      {/* Chat reply input — shown only during active conversation */}
      {phase === 'chatting' && (
        <div style={s.replyBar}>
          <textarea
            ref={replyRef}
            style={s.replyInput}
            rows={1}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Reply…"
            disabled={isStreaming}
          />
          <button
            style={{ ...s.sendBtn, opacity: input.trim() && !isStreaming ? 1 : 0.35 }}
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || isStreaming}
            aria-label="Send"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3 9h12M9 3l6 6-6 6" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

    </div>
  );
}

// ─── Result card ───────────────────────────────────────────────────────────────

function ResultCard({ result, navigate, user, onAuthPrompt }) {
  const isSpecialist = result.careType === 'specialist';
  const isUrgent     = result.level === 'urgent';
  const isSoon       = result.level === 'soon' && !isSpecialist;

  const cfg = isSpecialist
    ? { bg: 'var(--teal-faint)', icon: '🔬', label: 'Find a specialist', action: 'We\'ll match you with the right provider' }
    : {
        urgent: { bg: '#fef3f0', icon: '🚨', label: 'Please seek care now',          action: 'Don\'t wait on this one' },
        soon:   { bg: '#fdf0e6', icon: '📅', label: 'See a doctor soon',             action: 'Within the next 1–2 days' },
        home:   { bg: 'var(--teal-faint)', icon: '🏠', label: 'Manageable at home', action: 'Rest, fluids, and monitor' },
      }[result.level] || { bg: 'var(--teal-faint)', icon: '📋', label: 'Guidance ready', action: '' };

  const arrowIcon = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  function goToFindCare() {
    navigate('findCare', { providerContext: {
      resultLevel:   result.level,
      careType:      result.careType,
      specialtyHint: result.specialty || null,
      icd10:         result.icd10     || null,
      condition:     result.condition || null,
    }});
  }

  return (
    <>
      <div style={{ ...s.resultCard, marginBottom: '12px' }}>
        <div style={{ ...s.resultBand, background: cfg.bg }}>
          <div style={s.resultIcon}>{cfg.icon}</div>
          <div>
            <div style={s.resultLevel}>{cfg.label}</div>
            <div style={s.resultAction}>{cfg.action}</div>
          </div>
        </div>

        {/* Condition pill — suppressed for urgent results */}
        {!isUrgent && result.condition && (
          <div style={{ padding: '12px 20px 0' }}>
            <div style={s.conditionPill}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <div style={s.conditionDot} />
                <span style={{ fontSize: '12px', color: 'var(--ink-3)' }}>This sounds like:</span>
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }}>{result.condition}</span>
              </div>
              <div style={{ fontSize: '10.5px', color: 'var(--ink-4)', lineHeight: 1.4, paddingLeft: '13px', marginTop: '3px' }}>
                Based on your description — not a diagnosis
              </div>
            </div>
          </div>
        )}

        <div style={s.resultBody}>
          <p style={s.resultP}>{result.reason}</p>
          {isUrgent && (
            <p style={{ ...s.resultP, color: 'var(--ink-3)', fontSize: '12.5px' }}>
              If symptoms are severe or worsening quickly, <strong>call 911</strong> rather than driving yourself.
            </p>
          )}
        </div>
      </div>

      <div style={s.handoff}>
        <div style={s.handoffNudge}>
          <div style={s.handoffLabel}>
            {isUrgent ? 'Your next step' : isSpecialist ? 'Let\'s find the right specialist' : isSoon ? 'Let\'s make this easy' : 'Good to know'}
          </div>
          <p style={s.handoffText}>
            {isUrgent    && 'We want to get you in front of someone quickly. Find urgent care near you — most are walk-in and open now.'}
            {isSpecialist && 'We\'ll show you specialists in Greenville who are accepting patients.'}
            {isSoon      && 'You need to see someone in the next day or two. Where do you want to start?'}
            {!isUrgent && !isSpecialist && !isSoon && 'While you\'re here — Greenville has great free and low-cost health resources if you ever need them.'}
          </p>
        </div>

        {isUrgent ? (
          <div style={s.handoffSingle}>
            <button style={{ ...s.primaryBtn, background: 'var(--teal)', margin: 0 }} onClick={goToFindCare}>
              Find urgent care in Greenville →
            </button>
            <p style={{ textAlign: 'center', fontSize: '12px', color: 'var(--ink-4)', marginTop: '8px' }}>Or call 911 if symptoms are severe</p>
          </div>
        ) : isSpecialist ? (
          <div style={s.handoffSingle}>
            <button style={{ ...s.primaryBtn, background: 'var(--teal)', margin: 0 }} onClick={goToFindCare}>
              Find a specialist in Greenville →
            </button>
          </div>
        ) : (
          <div style={s.handoffActions}>
            <button style={s.handoffAction} onClick={goToFindCare}>
              <div style={s.handoffActionLeft}>
                <div style={s.handoffActionTitle}>Find a doctor in Greenville</div>
                <div style={s.handoffActionDesc}>We'll show you who's available and accepting patients</div>
              </div>
              {arrowIcon}
            </button>
            <button style={s.handoffAction} onClick={() => navigate('insurance')}>
              <div style={s.handoffActionLeft}>
                <div style={s.handoffActionTitle}>Check what this visit will cost first</div>
                <div style={s.handoffActionDesc}>See your copay, deductible, and what you'll actually pay</div>
              </div>
              {arrowIcon}
            </button>
          </div>
        )}
      </div>

      {!isUrgent && !user && onAuthPrompt && (
        <div style={s.followUpNudge}>
          <div style={s.followUpContent}>
            <div style={s.followUpTitle}>Get a 48-hour follow-up</div>
            <p style={s.followUpText}>We'll check in to see how you're doing and remind you if you still need care.</p>
          </div>
          <button style={s.followUpBtn} onClick={onAuthPrompt}>
            Sign up — free
          </button>
        </div>
      )}
    </>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const s = {
  screen:      { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--cream)' },

  header:      { padding: '52px 24px 20px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', position: 'relative', flexShrink: 0 },
  accentBar:   { position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, var(--teal), var(--teal-2))' },
  back:        { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, color: 'var(--ink-3)', cursor: 'pointer', marginBottom: '16px', background: 'none', border: 'none', padding: 0, fontFamily: 'var(--font-body)' },
  title:       { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '26px', color: 'var(--ink)', letterSpacing: '-0.5px', lineHeight: 1.2 },
  subtitle:    { fontSize: '13.5px', color: 'var(--ink-3)', marginTop: '6px', lineHeight: 1.5 },

  disclaimer:  { display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 20px', background: 'var(--cream-2)', borderBottom: '1px solid var(--border)', fontSize: '11px', color: 'var(--ink-4)', lineHeight: 1.4, flexShrink: 0 },

  body:        { flex: 1, minHeight: 0, overflowY: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none' },
  bodyInner:   { display: 'flex', flexDirection: 'column', gap: '16px', padding: '24px' },

  section:     { display: 'flex', flexDirection: 'column', gap: '8px' },
  sectionLabel:{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--teal)' },
  textarea:    { width: '100%', padding: '14px 16px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--border-strong)', background: 'var(--surface)', fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--ink)', resize: 'none', outline: 'none', lineHeight: 1.5, boxSizing: 'border-box' },
  primaryBtn:  { width: '100%', padding: '16px 20px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--teal)', color: 'white', fontFamily: 'var(--font-body)', fontSize: '15px', fontWeight: 600, cursor: 'pointer' },

  // Chat
  chatCard:      { background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '20px 16px', boxShadow: 'var(--shadow-sm)' },
  chat:          { display: 'flex', flexDirection: 'column', gap: '14px' },
  bubbleWrap:    { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '88%' },
  bubbleWrapUser:{ alignSelf: 'flex-end', alignItems: 'flex-end' },
  bubbleFrom:    { fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--teal)', marginBottom: '4px', paddingLeft: '2px' },
  bubble:        { padding: '12px 16px', borderRadius: 'var(--radius-md)', fontSize: '14px', lineHeight: 1.65, fontFamily: 'var(--font-body)', color: 'var(--ink)' },
  bubbleAssistant: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '12px 16px', fontSize: '14px', lineHeight: 1.65, fontFamily: 'var(--font-body)', color: 'var(--ink)' },
  bubbleUser:    { background: 'var(--teal)', color: 'white', border: 'none' },
  typingBubble:  { display: 'flex', gap: '5px', alignItems: 'center', padding: '14px 18px' },

  dot:     { width: '6px', height: '6px', borderRadius: '50%', background: 'var(--teal)', display: 'inline-block', animation: 'pulseDot 1.2s ease-in-out infinite' },
  cursor:  { display: 'inline-block', width: '2px', height: '14px', background: 'var(--teal)', marginLeft: '2px', verticalAlign: 'text-bottom', animation: 'blink 0.7s ease-in-out infinite' },

  errorMsg: { fontSize: '13px', color: '#c0392b', padding: '4px 2px' },

  // Reply bar
  replyBar:   { display: 'flex', alignItems: 'flex-end', gap: '8px', padding: '12px 16px 20px', background: 'var(--surface)', borderTop: '1px solid var(--border)', flexShrink: 0 },
  replyInput: { flex: 1, padding: '10px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--border-strong)', background: 'var(--cream)', fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--ink)', resize: 'none', outline: 'none', lineHeight: 1.5 },
  sendBtn:    { width: '42px', height: '42px', borderRadius: '50%', background: 'var(--teal)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },

  // Result card (matches existing design)
  resultCard:       { background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow-card)' },
  resultBand:       { padding: '20px 22px 18px', display: 'flex', alignItems: 'center', gap: '14px' },
  resultIcon:       { fontSize: '24px', flexShrink: 0 },
  resultLevel:      { fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-2)', marginBottom: '2px' },
  resultAction:     { fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '18px', color: 'var(--ink)', letterSpacing: '-0.3px' },
  resultBody:       { padding: '18px 22px', background: 'var(--surface)' },
  resultP:          { fontSize: '13.5px', color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: '10px' },

  handoff:          { background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-strong)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' },
  handoffNudge:     { padding: '16px 18px 12px', background: 'var(--teal-faint)', borderBottom: '1px solid rgba(30,92,84,0.12)' },
  handoffLabel:     { fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--teal)', marginBottom: '4px' },
  handoffText:      { fontSize: '13.5px', color: 'var(--ink)', lineHeight: 1.5 },
  handoffSingle:    { padding: '14px 18px' },
  handoffActions:   { display: 'flex', flexDirection: 'column' },
  handoffAction:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '15px 18px', background: 'var(--surface)', cursor: 'pointer', border: 'none', borderTop: '1px solid var(--border)', fontFamily: 'var(--font-body)', width: '100%', textAlign: 'left' },
  handoffActionLeft:{ flex: 1 },
  handoffActionTitle:{ fontSize: '14px', fontWeight: 600, color: 'var(--ink)', marginBottom: '2px' },
  handoffActionDesc: { fontSize: '12px', color: 'var(--ink-3)', lineHeight: 1.4 },

  // Condition pill
  conditionPill: { padding: '10px 14px', background: 'var(--teal-faint)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(30,92,84,0.14)' },
  conditionDot:  { width: '5px', height: '5px', borderRadius: '50%', background: 'var(--teal)', flexShrink: 0 },
  // Follow-up signup nudge
  followUpNudge:   { display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 18px', background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' },
  followUpContent: { flex: 1 },
  followUpTitle:   { fontSize: '13.5px', fontWeight: 600, color: 'var(--ink)', marginBottom: '3px' },
  followUpText:    { fontSize: '12px', color: 'var(--ink-3)', lineHeight: 1.4, margin: 0 },
  followUpBtn:     { flexShrink: 0, padding: '9px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--teal)', color: 'white', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
};
